import * as z from 'zod';

import * as Types from './graphql';

type Properties<T> = Required<{
  [K in keyof T]: z.ZodType<T[K]>;
}>;

type definedNonNullAny = {};

export const isDefinedNonNullAny = (v: any): v is definedNonNullAny => v !== undefined && v !== null;

export const definedNonNullAnySchema = z.any().refine((v) => isDefinedNonNullAny(v));

export const ModelSortDirectionSchema = z.enum(Types.ModelSortDirection);

export function CardDriverRankingDriverInfoInputSchema(): z.ZodObject<
  Properties<Types.CardDriverRankingDriverInfoInput>
> {
  return z.object({
    customDriverName: z.string().nullish(),
    customDriverPageUrl: z.string().nullish(),
    customDriverSurname: z.string().nullish(),
    flagUrl: z.string().nullish(),
    pos: z.string().nullish(),
    pts: z.string().nullish(),
  });
}

export function CardGpCalendarGpInfoInputSchema(): z.ZodObject<Properties<Types.CardGpCalendarGpInfoInput>> {
  return z.object({
    customGpName: z.string().nullish(),
    endDateTime: z.string().nullish(),
    flagUrl: z.string().nullish(),
    isExclusive: z.boolean().nullish(),
    lgpPreviewUrl: z.string().nullish(),
    startDateTime: z.string().nullish(),
  });
}

export function CardGpResultsResultInfoInputSchema(): z.ZodObject<Properties<Types.CardGpResultsResultInfoInput>> {
  return z.object({
    customDriverName: z.string().nullish(),
    customDriverPageUrl: z.string().nullish(),
    customDriverSurname: z.string().nullish(),
    flagUrl: z.string().nullish(),
    gap: z.string().nullish(),
    pos: z.string().nullish(),
    timing: z.lazy(() => CardGpResultsResultInfoTimingInputSchema().nullish()),
  });
}

export function CardGpResultsResultInfoTimingInputSchema(): z.ZodObject<
  Properties<Types.CardGpResultsResultInfoTimingInput>
> {
  return z.object({
    timeHours: z.string().nullish(),
    timeMilliseconds: z.string().nullish(),
    timeMinutes: z.string().nullish(),
    timeSeconds: z.string().nullish(),
  });
}

export function CardTeamRankingTeamInfoInputSchema(): z.ZodObject<Properties<Types.CardTeamRankingTeamInfoInput>> {
  return z.object({
    customTeamName: z.string().nullish(),
    customTeamPageUrl: z.string().nullish(),
    logoUrl: z.string().nullish(),
    pos: z.string().nullish(),
    pts: z.string().nullish(),
  });
}

export function CreateCardDriverRankingWidgetInputSchema(): z.ZodObject<
  Properties<Types.CreateCardDriverRankingWidgetInput>
> {
  return z.object({
    competitionId: z.string(),
    customCompetitionName: z.string().nullish(),
    drivers: z.array(z.lazy(() => CardDriverRankingDriverInfoInputSchema().nullable())).nullish(),
    rankingLink: z.string().nullish(),
  });
}

export function CreateCardGpCalendarWidgetInputSchema(): z.ZodObject<
  Properties<Types.CreateCardGpCalendarWidgetInput>
> {
  return z.object({
    calendarLink: z.string().nullish(),
    competitionId: z.string(),
    customCompetitionName: z.string().nullish(),
    gpList: z.array(z.lazy(() => CardGpCalendarGpInfoInputSchema().nullable())).nullish(),
  });
}

export function CreateCardGpResultsWidgetInputSchema(): z.ZodObject<Properties<Types.CreateCardGpResultsWidgetInput>> {
  return z.object({
    competitionId: z.string(),
    customCompetitionName: z.string().nullish(),
    customGpName: z.string().nullish(),
    isSprint: z.boolean().nullish(),
    results: z.array(z.lazy(() => CardGpResultsResultInfoInputSchema().nullable())).nullish(),
    resultsLink: z.string().nullish(),
  });
}

export function CreateCardTeamRankingWidgetInputSchema(): z.ZodObject<
  Properties<Types.CreateCardTeamRankingWidgetInput>
> {
  return z.object({
    competitionId: z.string(),
    customCompetitionName: z.string().nullish(),
    rankingLink: z.string().nullish(),
    teams: z.array(z.lazy(() => CardTeamRankingTeamInfoInputSchema().nullable())).nullish(),
  });
}

export function CreateCompetitionInputSchema(): z.ZodObject<Properties<Types.CreateCompetitionInput>> {
  return z.object({
    competitionId: z.string(),
    competitionJson: z.any(),
    editorialDataId: z.string(),
    previousSeasonAndCompId: z.string().nullish(),
    seasonAndCompId: z.string(),
    seasonId: z.string(),
  });
}

export function CreateCrossLinkInputSchema(): z.ZodObject<Properties<Types.CreateCrossLinkInput>> {
  return z.object({
    crossLinkJson: z.any(),
    gpId: z.string(),
    seasonAndCompId: z.string(),
  });
}

export function CreateDriverInputSchema(): z.ZodObject<Properties<Types.CreateDriverInput>> {
  return z.object({
    driverId: z.string(),
    driverJson: z.any(),
    editorialDataId: z.string(),
    seasonAndCompId: z.string(),
    teamId: z.string(),
  });
}

export function CreateDriverRankingWidgetInputSchema(): z.ZodObject<Properties<Types.CreateDriverRankingWidgetInput>> {
  return z.object({
    competitionId: z.string(),
    competitionLabel: z.string().nullish(),
    items: z.array(z.lazy(() => DriverRankingDriverInputSchema().nullable())).nullish(),
    seasonAndCompId: z.string(),
    seasonId: z.string(),
  });
}

export function CreateDriverStatsWidgetInputSchema(): z.ZodObject<Properties<Types.CreateDriverStatsWidgetInput>> {
  return z.object({
    customCompetitionName: z.string().nullish(),
    customSeasonName: z.string().nullish(),
    driverId: z.string(),
    seasonAndCompId: z.string().nullish(),
    statistics: z.array(z.lazy(() => DriverStatInputSchema().nullable())).nullish(),
  });
}

export function CreateEditorialDataInputSchema(): z.ZodObject<Properties<Types.CreateEditorialDataInput>> {
  return z.object({
    editorialDataId: z.string(),
    editorialJson: z.any(),
    isCurrent: z.boolean().nullish(),
    seasonAndCompId: z.string(),
  });
}

export function CreateGpCalendarWidgetInputSchema(): z.ZodObject<Properties<Types.CreateGpCalendarWidgetInput>> {
  return z.object({
    competitionId: z.string(),
    items: z.array(z.lazy(() => GpCalendarItemInputSchema().nullable())).nullish(),
    seasonAndCompId: z.string(),
    seasonId: z.string(),
  });
}

export function CreateGpCurrentCalendarWidgetInputSchema(): z.ZodObject<
  Properties<Types.CreateGpCurrentCalendarWidgetInput>
> {
  return z.object({
    competitionId: z.string(),
    items: z.array(z.lazy(() => GpCalendarItemInputSchema().nullable())).nullish(),
    seasonAndCompId: z.string(),
    seasonId: z.string(),
  });
}

export function CreateGpDataInputSchema(): z.ZodObject<Properties<Types.CreateGpDataInput>> {
  return z.object({
    competitionId: z.string(),
    data: z.any(),
    gpId: z.string(),
    messageType: z.string(),
    seasonAndCompId: z.string(),
    seasonId: z.string(),
  });
}

export function CreateGpLiveContainerWidgetInputSchema(): z.ZodObject<
  Properties<Types.CreateGpLiveContainerWidgetInput>
> {
  return z.object({
    DS: z.lazy(() => GpLiveContainerWrapperInputSchema().nullish()),
    G: z.lazy(() => GpLiveContainerWrapperInputSchema().nullish()),
    R: z.lazy(() => GpLiveContainerWrapperInputSchema().nullish()),
    TS: z.lazy(() => GpLiveContainerWrapperInputSchema().nullish()),
    competitionId: z.string(),
    seasonAndCompId: z.string(),
    seasonId: z.string(),
  });
}

export function CreateGpLiveHeroWidgetInputSchema(): z.ZodObject<Properties<Types.CreateGpLiveHeroWidgetInput>> {
  return z.object({
    competitionId: z.string(),
    gpInfo: z.lazy(() => GpLiveHeroGpInfoInputSchema().nullish()),
    isCurrent: z.boolean().nullish(),
    seasonAndCompId: z.string(),
    seasonId: z.string(),
  });
}

export function CreateGpPodiumListWidgetInputSchema(): z.ZodObject<Properties<Types.CreateGpPodiumListWidgetInput>> {
  return z.object({
    competitionId: z.string(),
    grandPrixList: z.array(z.lazy(() => GpPodiumListGrandPrixInputSchema().nullable())).nullish(),
    seasonAndCompId: z.string(),
    seasonId: z.string(),
  });
}

export function CreateGpResultsContainerWidgetInputSchema(): z.ZodObject<
  Properties<Types.CreateGpResultsContainerWidgetInput>
> {
  return z.object({
    FP1: z.lazy(() => GpResultsContainerWrapperInputSchema().nullish()),
    FP2: z.lazy(() => GpResultsContainerWrapperInputSchema().nullish()),
    FP3: z.lazy(() => GpResultsContainerWrapperInputSchema().nullish()),
    FP4: z.lazy(() => GpResultsContainerWrapperInputSchema().nullish()),
    G: z.lazy(() => GpResultsContainerWrapperInputSchema().nullish()),
    GS: z.lazy(() => GpResultsContainerWrapperInputSchema().nullish()),
    Q: z.lazy(() => GpResultsContainerWrapperInputSchema().nullish()),
    QS: z.lazy(() => GpResultsContainerWrapperInputSchema().nullish()),
    R: z.lazy(() => GpResultsContainerWrapperInputSchema().nullish()),
    SPRINT: z.lazy(() => GpResultsContainerWrapperInputSchema().nullish()),
    SS: z.lazy(() => GpResultsContainerWrapperInputSchema().nullish()),
    competitionId: z.string(),
    gpId: z.string(),
    seasonAndCompId: z.string(),
    seasonId: z.string(),
  });
}

export function CreateGpResultsWidgetInputSchema(): z.ZodObject<Properties<Types.CreateGpResultsWidgetInput>> {
  return z.object({
    competitionId: z.string(),
    gpId: z.string(),
    gpInfo: z.lazy(() => GpResultsGpInfoInputSchema().nullish()),
    gpStatus: z.string().nullish(),
    isCurrent: z.boolean().nullish(),
    seasonAndCompId: z.string(),
    seasonId: z.string(),
    sessionName: z.string(),
    sessions: z.array(z.lazy(() => GpResultsSessionInputSchema().nullable())).nullish(),
  });
}

export function CreateGrandPrixInputSchema(): z.ZodObject<Properties<Types.CreateGrandPrixInput>> {
  return z.object({
    date: z.string().datetime().nullish(),
    editorialDataId: z.string(),
    gpId: z.string(),
    gpJson: z.any().nullish(),
    isCurrent: z.boolean().nullish(),
    seasonAndCompId: z.string(),
    status: z.string().nullish(),
  });
}

export function CreateLgpHeroStickyWidgetInputSchema(): z.ZodObject<Properties<Types.CreateLgpHeroStickyWidgetInput>> {
  return z.object({
    competitionId: z.string(),
    gpId: z.string(),
    info: z.lazy(() => LgpHeroStickyInfoInputSchema().nullish()),
    seasonAndCompId: z.string(),
    seasonId: z.string(),
    session: z.lazy(() => LgpHeroStickySessionInputSchema().nullish()),
  });
}

export function CreateLgpHeroWidgetInputSchema(): z.ZodObject<Properties<Types.CreateLgpHeroWidgetInput>> {
  return z.object({
    competitionId: z.string(),
    gpId: z.string(),
    info: z.lazy(() => LgpHeroInfoInputSchema().nullish()),
    seasonAndCompId: z.string(),
    seasonId: z.string(),
    session: z.lazy(() => LgpHeroSessionInputSchema().nullish()),
  });
}

export function CreateLgpPreviewWidgetInputSchema(): z.ZodObject<Properties<Types.CreateLgpPreviewWidgetInput>> {
  return z.object({
    calendar: z.lazy(() => LgpInfoLinkInputSchema().nullish()),
    competitionId: z.string(),
    gpId: z.string(),
    gpName: z.string().nullish(),
    gpStartDateTime: z.string().nullish(),
    laps: z.string().nullish(),
    lengthKm: z.string().nullish(),
    news: z.lazy(() => LgpInfoLinkInputSchema().nullish()),
    ranking: z.lazy(() => LgpInfoLinkInputSchema().nullish()),
    results: z.lazy(() => LgpInfoLinkInputSchema().nullish()),
    seasonAndCompId: z.string(),
    seasonId: z.string(),
    sessions: z.array(z.lazy(() => LgpPreviewSessionInputSchema().nullable())).nullish(),
    sportLabel: z.string().nullish(),
  });
}

export function CreateLgpResultsContainerWidgetInputSchema(): z.ZodObject<
  Properties<Types.CreateLgpResultsContainerWidgetInput>
> {
  return z.object({
    FP1: z.lazy(() => LgpResultsContainerWrapperInputSchema().nullish()),
    FP2: z.lazy(() => LgpResultsContainerWrapperInputSchema().nullish()),
    FP3: z.lazy(() => LgpResultsContainerWrapperInputSchema().nullish()),
    FP4: z.lazy(() => LgpResultsContainerWrapperInputSchema().nullish()),
    G: z.lazy(() => LgpResultsContainerWrapperInputSchema().nullish()),
    GS: z.lazy(() => LgpResultsContainerWrapperInputSchema().nullish()),
    Q: z.lazy(() => LgpResultsContainerWrapperInputSchema().nullish()),
    QS: z.lazy(() => LgpResultsContainerWrapperInputSchema().nullish()),
    R: z.lazy(() => LgpResultsContainerWrapperInputSchema().nullish()),
    SPRINT: z.lazy(() => LgpResultsContainerWrapperInputSchema().nullish()),
    SS: z.lazy(() => LgpResultsContainerWrapperInputSchema().nullish()),
    competitionId: z.string(),
    gpId: z.string(),
    seasonAndCompId: z.string(),
    seasonId: z.string(),
  });
}

export function CreateLgpResultsWidgetInputSchema(): z.ZodObject<Properties<Types.CreateLgpResultsWidgetInput>> {
  return z.object({
    calendar: z.lazy(() => LgpInfoLinkInputSchema().nullish()),
    competitionId: z.string(),
    gpId: z.string(),
    gpInfo: z.lazy(() => LgpResultsGpInfoInputSchema().nullish()),
    gpStatus: z.string().nullish(),
    isCurrent: z.boolean().nullish(),
    ranking: z.lazy(() => LgpInfoLinkInputSchema().nullish()),
    results: z.lazy(() => LgpInfoLinkInputSchema().nullish()),
    seasonAndCompId: z.string(),
    seasonId: z.string(),
    sessionName: z.string(),
    sessions: z.array(z.lazy(() => LgpResultsSessionInputSchema().nullable())).nullish(),
  });
}

export function CreateLiveScoreWidgetInputSchema(): z.ZodObject<Properties<Types.CreateLiveScoreWidgetInput>> {
  return z.object({
    currentDay: z.array(z.lazy(() => LiveScoreWidgetDayInputSchema().nullable())).nullish(),
    id: z.string().nullish(),
    nextDay: z.array(z.lazy(() => LiveScoreWidgetDayInputSchema().nullable())).nullish(),
    previousDay: z.array(z.lazy(() => LiveScoreWidgetDayInputSchema().nullable())).nullish(),
  });
}

export function CreateSeasonInputSchema(): z.ZodObject<Properties<Types.CreateSeasonInput>> {
  return z.object({
    seasonId: z.string(),
    seasonJson: z.any(),
  });
}

export function CreateSeasonRankingGalleryWidgetInputSchema(): z.ZodObject<
  Properties<Types.CreateSeasonRankingGalleryWidgetInput>
> {
  return z.object({
    competitionId: z.string(),
    competitionName: z.string().nullish(),
    competitionsSlugName: z.string().nullish(),
    drivers: z.array(z.lazy(() => SeasonDriverRankingInputSchema().nullable())).nullish(),
    seasonAndCompId: z.string(),
    seasonId: z.string(),
    teams: z.array(z.lazy(() => SeasonTeamRankingInputSchema().nullable())).nullish(),
  });
}

export function CreateSessionInputSchema(): z.ZodObject<Properties<Types.CreateSessionInput>> {
  return z.object({
    date: z.string().datetime().nullish(),
    gpId: z.string(),
    isCurrent: z.boolean().nullish(),
    seasonAndCompId: z.string(),
    sessionId: z.string(),
    sessionJson: z.any().nullish(),
    status: z.string().nullish(),
  });
}

export function CreateTeamDriverPageWidgetInputSchema(): z.ZodObject<
  Properties<Types.CreateTeamDriverPageWidgetInput>
> {
  return z.object({
    competitionId: z.string(),
    seasonAndCompId: z.string(),
    seasonId: z.string(),
    teamList: z.array(z.lazy(() => TeamDriverTeamInputSchema().nullable())).nullish(),
  });
}

export function CreateTeamInputSchema(): z.ZodObject<Properties<Types.CreateTeamInput>> {
  return z.object({
    editorialDataId: z.string(),
    seasonAndCompId: z.string(),
    teamId: z.string(),
    teamJson: z.any(),
  });
}

export function CreateTeamRankingWidgetInputSchema(): z.ZodObject<Properties<Types.CreateTeamRankingWidgetInput>> {
  return z.object({
    competitionId: z.string(),
    competitionLabel: z.string().nullish(),
    items: z.array(z.lazy(() => TeamRankingTeamInputSchema().nullable())).nullish(),
    seasonAndCompId: z.string(),
    seasonId: z.string(),
  });
}

export function DeleteCardDriverRankingWidgetInputSchema(): z.ZodObject<
  Properties<Types.DeleteCardDriverRankingWidgetInput>
> {
  return z.object({
    competitionId: z.string(),
  });
}

export function DeleteCardGpCalendarWidgetInputSchema(): z.ZodObject<
  Properties<Types.DeleteCardGpCalendarWidgetInput>
> {
  return z.object({
    competitionId: z.string(),
  });
}

export function DeleteCardGpResultsWidgetInputSchema(): z.ZodObject<Properties<Types.DeleteCardGpResultsWidgetInput>> {
  return z.object({
    competitionId: z.string(),
  });
}

export function DeleteCardTeamRankingWidgetInputSchema(): z.ZodObject<
  Properties<Types.DeleteCardTeamRankingWidgetInput>
> {
  return z.object({
    competitionId: z.string(),
  });
}

export function DeleteCompetitionInputSchema(): z.ZodObject<Properties<Types.DeleteCompetitionInput>> {
  return z.object({
    seasonAndCompId: z.string(),
  });
}

export function DeleteCrossLinkInputSchema(): z.ZodObject<Properties<Types.DeleteCrossLinkInput>> {
  return z.object({
    gpId: z.string(),
    seasonAndCompId: z.string(),
  });
}

export function DeleteDriverInputSchema(): z.ZodObject<Properties<Types.DeleteDriverInput>> {
  return z.object({
    driverId: z.string(),
    seasonAndCompId: z.string(),
  });
}

export function DeleteDriverRankingWidgetInputSchema(): z.ZodObject<Properties<Types.DeleteDriverRankingWidgetInput>> {
  return z.object({
    seasonAndCompId: z.string(),
  });
}

export function DeleteDriverStatsWidgetInputSchema(): z.ZodObject<Properties<Types.DeleteDriverStatsWidgetInput>> {
  return z.object({
    driverId: z.string(),
  });
}

export function DeleteEditorialDataInputSchema(): z.ZodObject<Properties<Types.DeleteEditorialDataInput>> {
  return z.object({
    editorialDataId: z.string(),
    seasonAndCompId: z.string(),
  });
}

export function DeleteGpCalendarWidgetInputSchema(): z.ZodObject<Properties<Types.DeleteGpCalendarWidgetInput>> {
  return z.object({
    seasonAndCompId: z.string(),
  });
}

export function DeleteGpCurrentCalendarWidgetInputSchema(): z.ZodObject<
  Properties<Types.DeleteGpCurrentCalendarWidgetInput>
> {
  return z.object({
    seasonAndCompId: z.string(),
  });
}

export function DeleteGpDataInputSchema(): z.ZodObject<Properties<Types.DeleteGpDataInput>> {
  return z.object({
    gpId: z.string(),
    seasonAndCompId: z.string(),
  });
}

export function DeleteGpLiveContainerWidgetInputSchema(): z.ZodObject<
  Properties<Types.DeleteGpLiveContainerWidgetInput>
> {
  return z.object({
    seasonAndCompId: z.string(),
  });
}

export function DeleteGpLiveHeroWidgetInputSchema(): z.ZodObject<Properties<Types.DeleteGpLiveHeroWidgetInput>> {
  return z.object({
    seasonAndCompId: z.string(),
  });
}

export function DeleteGpPodiumListWidgetInputSchema(): z.ZodObject<Properties<Types.DeleteGpPodiumListWidgetInput>> {
  return z.object({
    seasonAndCompId: z.string(),
  });
}

export function DeleteGpResultsContainerWidgetInputSchema(): z.ZodObject<
  Properties<Types.DeleteGpResultsContainerWidgetInput>
> {
  return z.object({
    gpId: z.string(),
    seasonAndCompId: z.string(),
  });
}

export function DeleteGpResultsWidgetInputSchema(): z.ZodObject<Properties<Types.DeleteGpResultsWidgetInput>> {
  return z.object({
    gpId: z.string(),
    sessionName: z.string(),
  });
}

export function DeleteGrandPrixInputSchema(): z.ZodObject<Properties<Types.DeleteGrandPrixInput>> {
  return z.object({
    gpId: z.string(),
    seasonAndCompId: z.string(),
  });
}

export function DeleteLgpHeroStickyWidgetInputSchema(): z.ZodObject<Properties<Types.DeleteLgpHeroStickyWidgetInput>> {
  return z.object({
    gpId: z.string(),
    seasonAndCompId: z.string(),
  });
}

export function DeleteLgpHeroWidgetInputSchema(): z.ZodObject<Properties<Types.DeleteLgpHeroWidgetInput>> {
  return z.object({
    gpId: z.string(),
    seasonAndCompId: z.string(),
  });
}

export function DeleteLgpPreviewWidgetInputSchema(): z.ZodObject<Properties<Types.DeleteLgpPreviewWidgetInput>> {
  return z.object({
    gpId: z.string(),
    seasonAndCompId: z.string(),
  });
}

export function DeleteLgpResultsContainerWidgetInputSchema(): z.ZodObject<
  Properties<Types.DeleteLgpResultsContainerWidgetInput>
> {
  return z.object({
    gpId: z.string(),
    seasonAndCompId: z.string(),
  });
}

export function DeleteLgpResultsWidgetInputSchema(): z.ZodObject<Properties<Types.DeleteLgpResultsWidgetInput>> {
  return z.object({
    gpId: z.string(),
    sessionName: z.string(),
  });
}

export function DeleteLiveScoreWidgetInputSchema(): z.ZodObject<Properties<Types.DeleteLiveScoreWidgetInput>> {
  return z.object({
    id: z.string(),
  });
}

export function DeleteSeasonInputSchema(): z.ZodObject<Properties<Types.DeleteSeasonInput>> {
  return z.object({
    seasonId: z.string(),
  });
}

export function DeleteSeasonRankingGalleryWidgetInputSchema(): z.ZodObject<
  Properties<Types.DeleteSeasonRankingGalleryWidgetInput>
> {
  return z.object({
    seasonAndCompId: z.string(),
  });
}

export function DeleteSessionInputSchema(): z.ZodObject<Properties<Types.DeleteSessionInput>> {
  return z.object({
    gpId: z.string(),
    sessionId: z.string(),
  });
}

export function DeleteTeamDriverPageWidgetInputSchema(): z.ZodObject<
  Properties<Types.DeleteTeamDriverPageWidgetInput>
> {
  return z.object({
    seasonAndCompId: z.string(),
  });
}

export function DeleteTeamInputSchema(): z.ZodObject<Properties<Types.DeleteTeamInput>> {
  return z.object({
    seasonAndCompId: z.string(),
    teamId: z.string(),
  });
}

export function DeleteTeamRankingWidgetInputSchema(): z.ZodObject<Properties<Types.DeleteTeamRankingWidgetInput>> {
  return z.object({
    seasonAndCompId: z.string(),
  });
}

export function DriverRankingDriverInfoInputSchema(): z.ZodObject<Properties<Types.DriverRankingDriverInfoInput>> {
  return z.object({
    customDriverName: z.string().nullish(),
    customDriverPageUrl: z.string().nullish(),
    customDriverSurname: z.string().nullish(),
    customTeamName: z.string().nullish(),
    customTeamPageUrl: z.string().nullish(),
    firstName: z.string().nullish(),
    flagUrl: z.string().nullish(),
    lastName: z.string().nullish(),
    nationality: z.string().nullish(),
    pos: z.string().nullish(),
    pts: z.string().nullish(),
  });
}

export function DriverRankingDriverInputSchema(): z.ZodObject<Properties<Types.DriverRankingDriverInput>> {
  return z.object({
    driverInfo: z.lazy(() => DriverRankingDriverInfoInputSchema().nullish()),
  });
}

export function DriverStatInputSchema(): z.ZodObject<Properties<Types.DriverStatInput>> {
  return z.object({
    type: z.string().nullish(),
    value: z.number().nullish(),
  });
}

export function GpCalendarGpInfoInputSchema(): z.ZodObject<Properties<Types.GpCalendarGpInfoInput>> {
  return z.object({
    circuitUrl: z.string().nullish(),
    customGpName: z.string().nullish(),
    flagUrl: z.string().nullish(),
    gpCrosslink: z.string().nullish(),
    gpPageUrl: z.string().nullish(),
    gpStatus: z.string().nullish(),
    isCurrent: z.boolean().nullish(),
    isExclusive: z.boolean().nullish(),
    isSprint: z.boolean().nullish(),
    lapRecord: z.string().nullish(),
    laps: z.string().nullish(),
    lengthKm: z.string().nullish(),
    startDateTime: z.string().nullish(),
  });
}

export function GpCalendarItemInputSchema(): z.ZodObject<Properties<Types.GpCalendarItemInput>> {
  return z.object({
    gpInfo: z.lazy(() => GpCalendarGpInfoInputSchema().nullish()),
    isCurrent: z.boolean().nullish(),
    sessions: z.lazy(() => GpCalendarItemSessionsInputSchema().nullish()),
  });
}

export function GpCalendarItemSessionsInputSchema(): z.ZodObject<Properties<Types.GpCalendarItemSessionsInput>> {
  return z.object({
    items: z.array(z.lazy(() => GpCalendarSessionInputSchema().nullable())).nullish(),
  });
}

export function GpCalendarSessionInfoInputSchema(): z.ZodObject<Properties<Types.GpCalendarSessionInfoInput>> {
  return z.object({
    isCurrent: z.boolean().nullish(),
    sessionLabel: z.string().nullish(),
    sessionName: z.string().nullish(),
    sessionStatus: z.string().nullish(),
    startDateTime: z.string().nullish(),
  });
}

export function GpCalendarSessionInputSchema(): z.ZodObject<Properties<Types.GpCalendarSessionInput>> {
  return z.object({
    isCurrent: z.boolean().nullish(),
    sessionInfo: z.lazy(() => GpCalendarSessionInfoInputSchema().nullish()),
    sessionLabel: z.string().nullish(),
    sessionResultUrl: z.string().nullish(),
    sessionStatus: z.string().nullish(),
  });
}

export function GpLiveContainerWrapperAttributesInputSchema(): z.ZodObject<
  Properties<Types.GpLiveContainerWrapperAttributesInput>
> {
  return z.object({
    tracking: z.string().nullish(),
    url: z.string().nullish(),
  });
}

export function GpLiveContainerWrapperInputSchema(): z.ZodObject<Properties<Types.GpLiveContainerWrapperInput>> {
  return z.object({
    customDataAttributes: z.lazy(() => GpLiveContainerWrapperAttributesInputSchema().nullish()),
    isActive: z.boolean().nullish(),
    ssi: z.string().nullish(),
    type: z.string().nullish(),
  });
}

export function GpLiveHeroGpInfoInputSchema(): z.ZodObject<Properties<Types.GpLiveHeroGpInfoInput>> {
  return z.object({
    circuitUrl: z.string().nullish(),
    customGpName: z.string().nullish(),
    flagUrl: z.string().nullish(),
    gpPageUrl: z.string().nullish(),
    gpStatus: z.string().nullish(),
    isCurrent: z.boolean().nullish(),
    isExclusive: z.boolean().nullish(),
    isSprint: z.boolean().nullish(),
    lapRecord: z.string().nullish(),
    laps: z.string().nullish(),
    lengthKm: z.string().nullish(),
    startDateTime: z.string().nullish(),
  });
}

export function GpPodiumListGpInfoInputSchema(): z.ZodObject<Properties<Types.GpPodiumListGpInfoInput>> {
  return z.object({
    customGpName: z.string().nullish(),
    gpCrosslink: z.string().nullish(),
    gpPageUrl: z.string().nullish(),
    gpStatus: z.string().nullish(),
    isCurrent: z.boolean().nullish(),
    isExclusive: z.boolean().nullish(),
    isSprint: z.boolean().nullish(),
    lapRecord: z.string().nullish(),
    laps: z.string().nullish(),
    lengthKm: z.string().nullish(),
    startDateTime: z.string().nullish(),
  });
}

export function GpPodiumListGrandPrixInputSchema(): z.ZodObject<Properties<Types.GpPodiumListGrandPrixInput>> {
  return z.object({
    gpInfo: z.lazy(() => GpPodiumListGpInfoInputSchema().nullish()),
    gpStatus: z.string().nullish(),
    isCurrent: z.boolean().nullish(),
    session: z.lazy(() => GpPodiumListSessionInputSchema().nullish()),
  });
}

export function GpPodiumListSessionInfoDetailsInputSchema(): z.ZodObject<
  Properties<Types.GpPodiumListSessionInfoDetailsInput>
> {
  return z.object({
    customDriverName: z.string().nullish(),
    customDriverPageUrl: z.string().nullish(),
    customDriverSurname: z.string().nullish(),
    customTeamName: z.string().nullish(),
    customTeamPageUrl: z.string().nullish(),
    fastestLap: z.string().nullish(),
    firstName: z.string().nullish(),
    flagUrl: z.string().nullish(),
    gap: z.string().nullish(),
    isFastestLap: z.boolean().nullish(),
    laps: z.string().nullish(),
    lastName: z.string().nullish(),
    points: z.lazy(() => GpPodiumListSessionInfoDetailsPointsInputSchema().nullish()),
    position: z.string().nullish(),
    status: z.string().nullish(),
    team: z.lazy(() => GpPodiumListSessionInfoDetailsTeamInputSchema().nullish()),
    timing: z.lazy(() => GpPodiumListSessionInfoDetailsTimingInputSchema().nullish()),
  });
}

export function GpPodiumListSessionInfoDetailsPointsInputSchema(): z.ZodObject<
  Properties<Types.GpPodiumListSessionInfoDetailsPointsInput>
> {
  return z.object({
    personPoints: z.string().nullish(),
    teamPoints: z.string().nullish(),
  });
}

export function GpPodiumListSessionInfoDetailsTeamInputSchema(): z.ZodObject<
  Properties<Types.GpPodiumListSessionInfoDetailsTeamInput>
> {
  return z.object({
    name: z.string().nullish(),
  });
}

export function GpPodiumListSessionInfoDetailsTimingInputSchema(): z.ZodObject<
  Properties<Types.GpPodiumListSessionInfoDetailsTimingInput>
> {
  return z.object({
    timeHours: z.string().nullish(),
    timeMilliseconds: z.string().nullish(),
    timeMinutes: z.string().nullish(),
    timeSeconds: z.string().nullish(),
  });
}

export function GpPodiumListSessionInfoInputSchema(): z.ZodObject<Properties<Types.GpPodiumListSessionInfoInput>> {
  return z.object({
    details: z.array(z.lazy(() => GpPodiumListSessionInfoDetailsInputSchema().nullable())).nullish(),
    isCurrent: z.boolean().nullish(),
    sessionLabel: z.string().nullish(),
    sessionName: z.string().nullish(),
    sessionStatus: z.string().nullish(),
    startDateTime: z.string().nullish(),
  });
}

export function GpPodiumListSessionInputSchema(): z.ZodObject<Properties<Types.GpPodiumListSessionInput>> {
  return z.object({
    isCurrent: z.boolean().nullish(),
    sessionInfo: z.lazy(() => GpPodiumListSessionInfoInputSchema().nullish()),
    sessionLabel: z.string().nullish(),
    sessionStatus: z.string().nullish(),
  });
}

export function GpResultsContainerWrapperAttributesInputSchema(): z.ZodObject<
  Properties<Types.GpResultsContainerWrapperAttributesInput>
> {
  return z.object({
    tracking: z.string().nullish(),
    url: z.string().nullish(),
  });
}

export function GpResultsContainerWrapperInputSchema(): z.ZodObject<Properties<Types.GpResultsContainerWrapperInput>> {
  return z.object({
    customDataAttributes: z.lazy(() => GpResultsContainerWrapperAttributesInputSchema().nullish()),
    ssi: z.string().nullish(),
    startDateTime: z.string().nullish(),
  });
}

export function GpResultsGpInfoInputSchema(): z.ZodObject<Properties<Types.GpResultsGpInfoInput>> {
  return z.object({
    customGpName: z.string().nullish(),
    gpStatus: z.string().nullish(),
    isCurrent: z.boolean().nullish(),
    isExclusive: z.boolean().nullish(),
    isSprint: z.boolean().nullish(),
    lapRecord: z.string().nullish(),
    laps: z.string().nullish(),
    lengthKm: z.string().nullish(),
    startDateTime: z.string().nullish(),
  });
}

export function GpResultsSessionInfoDetailsInputSchema(): z.ZodObject<
  Properties<Types.GpResultsSessionInfoDetailsInput>
> {
  return z.object({
    customDriverName: z.string().nullish(),
    customDriverPageUrl: z.string().nullish(),
    customDriverSurname: z.string().nullish(),
    customTeamName: z.string().nullish(),
    customTeamPageUrl: z.string().nullish(),
    fastestLap: z.string().nullish(),
    firstName: z.string().nullish(),
    flagUrl: z.string().nullish(),
    gap: z.string().nullish(),
    isFastestLap: z.boolean().nullish(),
    laps: z.string().nullish(),
    lastName: z.string().nullish(),
    points: z.lazy(() => GpResultsSessionInfoDetailsPointsInputSchema().nullish()),
    position: z.string().nullish(),
    status: z.string().nullish(),
    team: z.lazy(() => GpResultsSessionInfoDetailsTeamInputSchema().nullish()),
    timing: z.lazy(() => GpResultsSessionInfoDetailsTimingInputSchema().nullish()),
  });
}

export function GpResultsSessionInfoDetailsPointsInputSchema(): z.ZodObject<
  Properties<Types.GpResultsSessionInfoDetailsPointsInput>
> {
  return z.object({
    personPoints: z.string().nullish(),
    teamPoints: z.string().nullish(),
  });
}

export function GpResultsSessionInfoDetailsTeamInputSchema(): z.ZodObject<
  Properties<Types.GpResultsSessionInfoDetailsTeamInput>
> {
  return z.object({
    name: z.string().nullish(),
  });
}

export function GpResultsSessionInfoDetailsTimingInputSchema(): z.ZodObject<
  Properties<Types.GpResultsSessionInfoDetailsTimingInput>
> {
  return z.object({
    timeHours: z.string().nullish(),
    timeMilliseconds: z.string().nullish(),
    timeMinutes: z.string().nullish(),
    timeSeconds: z.string().nullish(),
  });
}

export function GpResultsSessionInfoInputSchema(): z.ZodObject<Properties<Types.GpResultsSessionInfoInput>> {
  return z.object({
    details: z.array(z.lazy(() => GpResultsSessionInfoDetailsInputSchema().nullable())).nullish(),
    isCurrent: z.boolean().nullish(),
    sessionLabel: z.string().nullish(),
    sessionName: z.string().nullish(),
    sessionStatus: z.string().nullish(),
    startDateTime: z.string().nullish(),
  });
}

export function GpResultsSessionInputSchema(): z.ZodObject<Properties<Types.GpResultsSessionInput>> {
  return z.object({
    isCurrent: z.boolean().nullish(),
    sessionInfo: z.lazy(() => GpResultsSessionInfoInputSchema().nullish()),
  });
}

export function LgpHeroInfoInputSchema(): z.ZodObject<Properties<Types.LgpHeroInfoInput>> {
  return z.object({
    circuitImgUrl: z.string().nullish(),
    customGpName: z.string().nullish(),
    endDate: z.string().nullish(),
    flagImgUrl: z.string().nullish(),
    sportTag: z.string().nullish(),
    startDate: z.string().nullish(),
  });
}

export function LgpHeroPodiumInputSchema(): z.ZodObject<Properties<Types.LgpHeroPodiumInput>> {
  return z.object({
    customDriverName: z.string().nullish(),
    customDriverShortName: z.string().nullish(),
    customDriverSurname: z.string().nullish(),
    driverImgUrl: z.string().nullish(),
    flagImgUrl: z.string().nullish(),
    position: z.number().nullish(),
  });
}

export function LgpHeroSessionInputSchema(): z.ZodObject<Properties<Types.LgpHeroSessionInput>> {
  return z.object({
    date: z.string().nullish(),
    label: z.string().nullish(),
    name: z.string().nullish(),
    podium: z.array(z.lazy(() => LgpHeroPodiumInputSchema())).nullish(),
    status: z.string().nullish(),
  });
}

export function LgpHeroStickyInfoInputSchema(): z.ZodObject<Properties<Types.LgpHeroStickyInfoInput>> {
  return z.object({
    customGpName: z.string().nullish(),
    endDate: z.string().nullish(),
    flagImgUrl: z.string().nullish(),
    startDate: z.string().nullish(),
  });
}

export function LgpHeroStickySessionInputSchema(): z.ZodObject<Properties<Types.LgpHeroStickySessionInput>> {
  return z.object({
    label: z.string().nullish(),
    name: z.string().nullish(),
    status: z.string().nullish(),
  });
}

export function LgpInfoLinkInputSchema(): z.ZodObject<Properties<Types.LgpInfoLinkInput>> {
  return z.object({
    link: z.string().nullish(),
    text: z.string().nullish(),
  });
}

export function LgpPreviewSessionInputSchema(): z.ZodObject<Properties<Types.LgpPreviewSessionInput>> {
  return z.object({
    date: z.string().nullish(),
    sessionName: z.string().nullish(),
  });
}

export function LgpResultsContainerWrapperAttributesInputSchema(): z.ZodObject<
  Properties<Types.LgpResultsContainerWrapperAttributesInput>
> {
  return z.object({
    tracking: z.string().nullish(),
    url: z.string().nullish(),
  });
}

export function LgpResultsContainerWrapperInputSchema(): z.ZodObject<
  Properties<Types.LgpResultsContainerWrapperInput>
> {
  return z.object({
    customDataAttributes: z.lazy(() => LgpResultsContainerWrapperAttributesInputSchema().nullish()),
    ssi: z.string().nullish(),
    startDateTime: z.string().nullish(),
  });
}

export function LgpResultsGpInfoInputSchema(): z.ZodObject<Properties<Types.LgpResultsGpInfoInput>> {
  return z.object({
    customGpName: z.string().nullish(),
    gpStatus: z.string().nullish(),
    isCurrent: z.boolean().nullish(),
    isExclusive: z.boolean().nullish(),
    isSprint: z.boolean().nullish(),
    lapRecord: z.string().nullish(),
    laps: z.string().nullish(),
    lengthKm: z.string().nullish(),
    startDateTime: z.string().nullish(),
  });
}

export function LgpResultsSessionInfoDetailsInputSchema(): z.ZodObject<
  Properties<Types.LgpResultsSessionInfoDetailsInput>
> {
  return z.object({
    customDriverName: z.string().nullish(),
    customDriverPageUrl: z.string().nullish(),
    customDriverSurname: z.string().nullish(),
    customTeamName: z.string().nullish(),
    customTeamPageUrl: z.string().nullish(),
    fastestLap: z.string().nullish(),
    flagUrl: z.string().nullish(),
    gap: z.string().nullish(),
    isFastestLap: z.boolean().nullish(),
    laps: z.string().nullish(),
    position: z.string().nullish(),
    status: z.string().nullish(),
    timing: z.lazy(() => LgpResultsSessionInfoDetailsTimingInputSchema().nullish()),
  });
}

export function LgpResultsSessionInfoDetailsTimingInputSchema(): z.ZodObject<
  Properties<Types.LgpResultsSessionInfoDetailsTimingInput>
> {
  return z.object({
    timeHours: z.string().nullish(),
    timeMilliseconds: z.string().nullish(),
    timeMinutes: z.string().nullish(),
    timeSeconds: z.string().nullish(),
  });
}

export function LgpResultsSessionInfoInputSchema(): z.ZodObject<Properties<Types.LgpResultsSessionInfoInput>> {
  return z.object({
    details: z.array(z.lazy(() => LgpResultsSessionInfoDetailsInputSchema().nullable())).nullish(),
    isCurrent: z.boolean().nullish(),
    sessionLabel: z.string().nullish(),
    sessionName: z.string().nullish(),
    sessionStatus: z.string().nullish(),
    startDateTime: z.string().nullish(),
  });
}

export function LgpResultsSessionInputSchema(): z.ZodObject<Properties<Types.LgpResultsSessionInput>> {
  return z.object({
    isCurrent: z.boolean().nullish(),
    sessionInfo: z.lazy(() => LgpResultsSessionInfoInputSchema().nullish()),
  });
}

export function LiveScoreWidgetDayInputSchema(): z.ZodObject<Properties<Types.LiveScoreWidgetDayInput>> {
  return z.object({
    gpInfo: z.lazy(() => LiveScoreWidgetGpInfoInputSchema().nullish()),
    sessions: z.array(z.lazy(() => LiveScoreWidgetSessionInputSchema().nullable())).nullish(),
    sportTag: z.string().nullish(),
  });
}

export function LiveScoreWidgetGpInfoInputSchema(): z.ZodObject<Properties<Types.LiveScoreWidgetGpInfoInput>> {
  return z.object({
    calendarUrl: z.string().nullish(),
    customGpName: z.string().nullish(),
    gpCrosslink: z.string().nullish(),
    gpFlag: z.string().nullish(),
    gpPageUrl: z.string().nullish(),
    rankingUrl: z.string().nullish(),
  });
}

export function LiveScoreWidgetResultsInputSchema(): z.ZodObject<Properties<Types.LiveScoreWidgetResultsInput>> {
  return z.object({
    customDriverName: z.string().nullish(),
    customDriverPageUrl: z.string().nullish(),
    customDriverSurname: z.string().nullish(),
    customTeamName: z.string().nullish(),
    customTeamPageUrl: z.string().nullish(),
    fastestLap: z.string().nullish(),
    firstName: z.string().nullish(),
    flagUrl: z.string().nullish(),
    gap: z.string().nullish(),
    isFastestLap: z.boolean().nullish(),
    lastName: z.string().nullish(),
    position: z.string().nullish(),
    teamName: z.string().nullish(),
    timing: z.lazy(() => LiveScoreWidgetResultsTimingInputSchema().nullish()),
  });
}

export function LiveScoreWidgetResultsTimingInputSchema(): z.ZodObject<
  Properties<Types.LiveScoreWidgetResultsTimingInput>
> {
  return z.object({
    timeHours: z.string().nullish(),
    timeMilliseconds: z.string().nullish(),
    timeMinutes: z.string().nullish(),
    timeSeconds: z.string().nullish(),
  });
}

export function LiveScoreWidgetSessionInputSchema(): z.ZodObject<Properties<Types.LiveScoreWidgetSessionInput>> {
  return z.object({
    results: z.array(z.lazy(() => LiveScoreWidgetResultsInputSchema().nullable())).nullish(),
    resultsUrl: z.string().nullish(),
    sessionDate: z.string().nullish(),
    sessionName: z.string().nullish(),
    sessionStatus: z.string().nullish(),
  });
}

export function ModelBooleanFilterInputSchema(): z.ZodObject<Properties<Types.ModelBooleanFilterInput>> {
  return z.object({
    eq: z.boolean().nullish(),
    ne: z.boolean().nullish(),
  });
}

export function ModelCardDriverRankingWidgetFilterInputSchema(): z.ZodObject<
  Properties<Types.ModelCardDriverRankingWidgetFilterInput>
> {
  return z.object({
    and: z.array(z.lazy(() => ModelCardDriverRankingWidgetFilterInputSchema().nullable())).nullish(),
    competitionId: z.lazy(() => ModelIDFilterInputSchema().nullish()),
    customCompetitionName: z.lazy(() => ModelStringFilterInputSchema().nullish()),
    not: z.lazy(() => ModelCardDriverRankingWidgetFilterInputSchema().nullish()),
    or: z.array(z.lazy(() => ModelCardDriverRankingWidgetFilterInputSchema().nullable())).nullish(),
    rankingLink: z.lazy(() => ModelStringFilterInputSchema().nullish()),
  });
}

export function ModelCardGpCalendarWidgetFilterInputSchema(): z.ZodObject<
  Properties<Types.ModelCardGpCalendarWidgetFilterInput>
> {
  return z.object({
    and: z.array(z.lazy(() => ModelCardGpCalendarWidgetFilterInputSchema().nullable())).nullish(),
    calendarLink: z.lazy(() => ModelStringFilterInputSchema().nullish()),
    competitionId: z.lazy(() => ModelIDFilterInputSchema().nullish()),
    customCompetitionName: z.lazy(() => ModelStringFilterInputSchema().nullish()),
    not: z.lazy(() => ModelCardGpCalendarWidgetFilterInputSchema().nullish()),
    or: z.array(z.lazy(() => ModelCardGpCalendarWidgetFilterInputSchema().nullable())).nullish(),
  });
}

export function ModelCardGpResultsWidgetFilterInputSchema(): z.ZodObject<
  Properties<Types.ModelCardGpResultsWidgetFilterInput>
> {
  return z.object({
    and: z.array(z.lazy(() => ModelCardGpResultsWidgetFilterInputSchema().nullable())).nullish(),
    competitionId: z.lazy(() => ModelIDFilterInputSchema().nullish()),
    customCompetitionName: z.lazy(() => ModelStringFilterInputSchema().nullish()),
    customGpName: z.lazy(() => ModelStringFilterInputSchema().nullish()),
    isSprint: z.lazy(() => ModelBooleanFilterInputSchema().nullish()),
    not: z.lazy(() => ModelCardGpResultsWidgetFilterInputSchema().nullish()),
    or: z.array(z.lazy(() => ModelCardGpResultsWidgetFilterInputSchema().nullable())).nullish(),
    resultsLink: z.lazy(() => ModelStringFilterInputSchema().nullish()),
  });
}

export function ModelCardTeamRankingWidgetFilterInputSchema(): z.ZodObject<
  Properties<Types.ModelCardTeamRankingWidgetFilterInput>
> {
  return z.object({
    and: z.array(z.lazy(() => ModelCardTeamRankingWidgetFilterInputSchema().nullable())).nullish(),
    competitionId: z.lazy(() => ModelIDFilterInputSchema().nullish()),
    customCompetitionName: z.lazy(() => ModelStringFilterInputSchema().nullish()),
    not: z.lazy(() => ModelCardTeamRankingWidgetFilterInputSchema().nullish()),
    or: z.array(z.lazy(() => ModelCardTeamRankingWidgetFilterInputSchema().nullable())).nullish(),
    rankingLink: z.lazy(() => ModelStringFilterInputSchema().nullish()),
  });
}

export function ModelCompetitionFilterInputSchema(): z.ZodObject<Properties<Types.ModelCompetitionFilterInput>> {
  return z.object({
    and: z.array(z.lazy(() => ModelCompetitionFilterInputSchema().nullable())).nullish(),
    competitionId: z.lazy(() => ModelIDFilterInputSchema().nullish()),
    competitionJson: z.lazy(() => ModelStringFilterInputSchema().nullish()),
    editorialDataId: z.lazy(() => ModelIDFilterInputSchema().nullish()),
    not: z.lazy(() => ModelCompetitionFilterInputSchema().nullish()),
    or: z.array(z.lazy(() => ModelCompetitionFilterInputSchema().nullable())).nullish(),
    previousSeasonAndCompId: z.lazy(() => ModelIDFilterInputSchema().nullish()),
    seasonAndCompId: z.lazy(() => ModelIDFilterInputSchema().nullish()),
    seasonId: z.lazy(() => ModelIDFilterInputSchema().nullish()),
  });
}

export function ModelCrossLinkFilterInputSchema(): z.ZodObject<Properties<Types.ModelCrossLinkFilterInput>> {
  return z.object({
    and: z.array(z.lazy(() => ModelCrossLinkFilterInputSchema().nullable())).nullish(),
    crossLinkJson: z.lazy(() => ModelStringFilterInputSchema().nullish()),
    gpId: z.lazy(() => ModelIDFilterInputSchema().nullish()),
    not: z.lazy(() => ModelCrossLinkFilterInputSchema().nullish()),
    or: z.array(z.lazy(() => ModelCrossLinkFilterInputSchema().nullable())).nullish(),
    seasonAndCompId: z.lazy(() => ModelIDFilterInputSchema().nullish()),
  });
}

export function ModelDriverFilterInputSchema(): z.ZodObject<Properties<Types.ModelDriverFilterInput>> {
  return z.object({
    and: z.array(z.lazy(() => ModelDriverFilterInputSchema().nullable())).nullish(),
    driverId: z.lazy(() => ModelIDFilterInputSchema().nullish()),
    driverJson: z.lazy(() => ModelStringFilterInputSchema().nullish()),
    editorialDataId: z.lazy(() => ModelIDFilterInputSchema().nullish()),
    not: z.lazy(() => ModelDriverFilterInputSchema().nullish()),
    or: z.array(z.lazy(() => ModelDriverFilterInputSchema().nullable())).nullish(),
    seasonAndCompId: z.lazy(() => ModelIDFilterInputSchema().nullish()),
    teamId: z.lazy(() => ModelIDFilterInputSchema().nullish()),
  });
}

export function ModelDriverRankingWidgetFilterInputSchema(): z.ZodObject<
  Properties<Types.ModelDriverRankingWidgetFilterInput>
> {
  return z.object({
    and: z.array(z.lazy(() => ModelDriverRankingWidgetFilterInputSchema().nullable())).nullish(),
    competitionId: z.lazy(() => ModelIDFilterInputSchema().nullish()),
    competitionLabel: z.lazy(() => ModelStringFilterInputSchema().nullish()),
    not: z.lazy(() => ModelDriverRankingWidgetFilterInputSchema().nullish()),
    or: z.array(z.lazy(() => ModelDriverRankingWidgetFilterInputSchema().nullable())).nullish(),
    seasonAndCompId: z.lazy(() => ModelIDFilterInputSchema().nullish()),
    seasonId: z.lazy(() => ModelIDFilterInputSchema().nullish()),
  });
}

export function ModelDriverStatsWidgetFilterInputSchema(): z.ZodObject<
  Properties<Types.ModelDriverStatsWidgetFilterInput>
> {
  return z.object({
    and: z.array(z.lazy(() => ModelDriverStatsWidgetFilterInputSchema().nullable())).nullish(),
    customCompetitionName: z.lazy(() => ModelStringFilterInputSchema().nullish()),
    customSeasonName: z.lazy(() => ModelStringFilterInputSchema().nullish()),
    driverId: z.lazy(() => ModelIDFilterInputSchema().nullish()),
    not: z.lazy(() => ModelDriverStatsWidgetFilterInputSchema().nullish()),
    or: z.array(z.lazy(() => ModelDriverStatsWidgetFilterInputSchema().nullable())).nullish(),
    seasonAndCompId: z.lazy(() => ModelStringFilterInputSchema().nullish()),
  });
}

export function ModelEditorialDataFilterInputSchema(): z.ZodObject<Properties<Types.ModelEditorialDataFilterInput>> {
  return z.object({
    and: z.array(z.lazy(() => ModelEditorialDataFilterInputSchema().nullable())).nullish(),
    editorialDataId: z.lazy(() => ModelIDFilterInputSchema().nullish()),
    editorialJson: z.lazy(() => ModelStringFilterInputSchema().nullish()),
    isCurrent: z.lazy(() => ModelBooleanFilterInputSchema().nullish()),
    not: z.lazy(() => ModelEditorialDataFilterInputSchema().nullish()),
    or: z.array(z.lazy(() => ModelEditorialDataFilterInputSchema().nullable())).nullish(),
    seasonAndCompId: z.lazy(() => ModelIDFilterInputSchema().nullish()),
  });
}

export function ModelFloatFilterInputSchema(): z.ZodObject<Properties<Types.ModelFloatFilterInput>> {
  return z.object({
    between: z.array(z.number().nullable()).nullish(),
    eq: z.number().nullish(),
    ge: z.number().nullish(),
    gt: z.number().nullish(),
    le: z.number().nullish(),
    lt: z.number().nullish(),
    ne: z.number().nullish(),
  });
}

export function ModelGpCalendarWidgetFilterInputSchema(): z.ZodObject<
  Properties<Types.ModelGpCalendarWidgetFilterInput>
> {
  return z.object({
    and: z.array(z.lazy(() => ModelGpCalendarWidgetFilterInputSchema().nullable())).nullish(),
    competitionId: z.lazy(() => ModelIDFilterInputSchema().nullish()),
    not: z.lazy(() => ModelGpCalendarWidgetFilterInputSchema().nullish()),
    or: z.array(z.lazy(() => ModelGpCalendarWidgetFilterInputSchema().nullable())).nullish(),
    seasonAndCompId: z.lazy(() => ModelIDFilterInputSchema().nullish()),
    seasonId: z.lazy(() => ModelIDFilterInputSchema().nullish()),
  });
}

export function ModelGpCurrentCalendarWidgetFilterInputSchema(): z.ZodObject<
  Properties<Types.ModelGpCurrentCalendarWidgetFilterInput>
> {
  return z.object({
    and: z.array(z.lazy(() => ModelGpCurrentCalendarWidgetFilterInputSchema().nullable())).nullish(),
    competitionId: z.lazy(() => ModelIDFilterInputSchema().nullish()),
    not: z.lazy(() => ModelGpCurrentCalendarWidgetFilterInputSchema().nullish()),
    or: z.array(z.lazy(() => ModelGpCurrentCalendarWidgetFilterInputSchema().nullable())).nullish(),
    seasonAndCompId: z.lazy(() => ModelIDFilterInputSchema().nullish()),
    seasonId: z.lazy(() => ModelIDFilterInputSchema().nullish()),
  });
}

export function ModelGpDataFilterInputSchema(): z.ZodObject<Properties<Types.ModelGpDataFilterInput>> {
  return z.object({
    and: z.array(z.lazy(() => ModelGpDataFilterInputSchema().nullable())).nullish(),
    competitionId: z.lazy(() => ModelIDFilterInputSchema().nullish()),
    data: z.lazy(() => ModelStringFilterInputSchema().nullish()),
    gpId: z.lazy(() => ModelIDFilterInputSchema().nullish()),
    messageType: z.lazy(() => ModelStringFilterInputSchema().nullish()),
    not: z.lazy(() => ModelGpDataFilterInputSchema().nullish()),
    or: z.array(z.lazy(() => ModelGpDataFilterInputSchema().nullable())).nullish(),
    seasonAndCompId: z.lazy(() => ModelIDFilterInputSchema().nullish()),
    seasonId: z.lazy(() => ModelIDFilterInputSchema().nullish()),
  });
}

export function ModelGpLiveContainerWidgetFilterInputSchema(): z.ZodObject<
  Properties<Types.ModelGpLiveContainerWidgetFilterInput>
> {
  return z.object({
    and: z.array(z.lazy(() => ModelGpLiveContainerWidgetFilterInputSchema().nullable())).nullish(),
    competitionId: z.lazy(() => ModelIDFilterInputSchema().nullish()),
    not: z.lazy(() => ModelGpLiveContainerWidgetFilterInputSchema().nullish()),
    or: z.array(z.lazy(() => ModelGpLiveContainerWidgetFilterInputSchema().nullable())).nullish(),
    seasonAndCompId: z.lazy(() => ModelIDFilterInputSchema().nullish()),
    seasonId: z.lazy(() => ModelIDFilterInputSchema().nullish()),
  });
}

export function ModelGpLiveHeroWidgetFilterInputSchema(): z.ZodObject<
  Properties<Types.ModelGpLiveHeroWidgetFilterInput>
> {
  return z.object({
    and: z.array(z.lazy(() => ModelGpLiveHeroWidgetFilterInputSchema().nullable())).nullish(),
    competitionId: z.lazy(() => ModelIDFilterInputSchema().nullish()),
    isCurrent: z.lazy(() => ModelBooleanFilterInputSchema().nullish()),
    not: z.lazy(() => ModelGpLiveHeroWidgetFilterInputSchema().nullish()),
    or: z.array(z.lazy(() => ModelGpLiveHeroWidgetFilterInputSchema().nullable())).nullish(),
    seasonAndCompId: z.lazy(() => ModelIDFilterInputSchema().nullish()),
    seasonId: z.lazy(() => ModelIDFilterInputSchema().nullish()),
  });
}

export function ModelGpPodiumListWidgetFilterInputSchema(): z.ZodObject<
  Properties<Types.ModelGpPodiumListWidgetFilterInput>
> {
  return z.object({
    and: z.array(z.lazy(() => ModelGpPodiumListWidgetFilterInputSchema().nullable())).nullish(),
    competitionId: z.lazy(() => ModelIDFilterInputSchema().nullish()),
    not: z.lazy(() => ModelGpPodiumListWidgetFilterInputSchema().nullish()),
    or: z.array(z.lazy(() => ModelGpPodiumListWidgetFilterInputSchema().nullable())).nullish(),
    seasonAndCompId: z.lazy(() => ModelIDFilterInputSchema().nullish()),
    seasonId: z.lazy(() => ModelIDFilterInputSchema().nullish()),
  });
}

export function ModelGpResultsContainerWidgetFilterInputSchema(): z.ZodObject<
  Properties<Types.ModelGpResultsContainerWidgetFilterInput>
> {
  return z.object({
    and: z.array(z.lazy(() => ModelGpResultsContainerWidgetFilterInputSchema().nullable())).nullish(),
    competitionId: z.lazy(() => ModelIDFilterInputSchema().nullish()),
    gpId: z.lazy(() => ModelIDFilterInputSchema().nullish()),
    not: z.lazy(() => ModelGpResultsContainerWidgetFilterInputSchema().nullish()),
    or: z.array(z.lazy(() => ModelGpResultsContainerWidgetFilterInputSchema().nullable())).nullish(),
    seasonAndCompId: z.lazy(() => ModelIDFilterInputSchema().nullish()),
    seasonId: z.lazy(() => ModelIDFilterInputSchema().nullish()),
  });
}

export function ModelGpResultsWidgetFilterInputSchema(): z.ZodObject<
  Properties<Types.ModelGpResultsWidgetFilterInput>
> {
  return z.object({
    and: z.array(z.lazy(() => ModelGpResultsWidgetFilterInputSchema().nullable())).nullish(),
    competitionId: z.lazy(() => ModelIDFilterInputSchema().nullish()),
    gpId: z.lazy(() => ModelIDFilterInputSchema().nullish()),
    gpStatus: z.lazy(() => ModelStringFilterInputSchema().nullish()),
    isCurrent: z.lazy(() => ModelBooleanFilterInputSchema().nullish()),
    not: z.lazy(() => ModelGpResultsWidgetFilterInputSchema().nullish()),
    or: z.array(z.lazy(() => ModelGpResultsWidgetFilterInputSchema().nullable())).nullish(),
    seasonAndCompId: z.lazy(() => ModelIDFilterInputSchema().nullish()),
    seasonId: z.lazy(() => ModelIDFilterInputSchema().nullish()),
    sessionName: z.lazy(() => ModelStringFilterInputSchema().nullish()),
  });
}

export function ModelGrandPrixFilterInputSchema(): z.ZodObject<Properties<Types.ModelGrandPrixFilterInput>> {
  return z.object({
    and: z.array(z.lazy(() => ModelGrandPrixFilterInputSchema().nullable())).nullish(),
    date: z.lazy(() => ModelStringFilterInputSchema().nullish()),
    editorialDataId: z.lazy(() => ModelIDFilterInputSchema().nullish()),
    gpId: z.lazy(() => ModelIDFilterInputSchema().nullish()),
    gpJson: z.lazy(() => ModelStringFilterInputSchema().nullish()),
    isCurrent: z.lazy(() => ModelBooleanFilterInputSchema().nullish()),
    not: z.lazy(() => ModelGrandPrixFilterInputSchema().nullish()),
    or: z.array(z.lazy(() => ModelGrandPrixFilterInputSchema().nullable())).nullish(),
    seasonAndCompId: z.lazy(() => ModelIDFilterInputSchema().nullish()),
    status: z.lazy(() => ModelStringFilterInputSchema().nullish()),
  });
}

export function ModelIDFilterInputSchema(): z.ZodObject<Properties<Types.ModelIDFilterInput>> {
  return z.object({
    beginsWith: z.string().nullish(),
    between: z.array(z.string().nullable()).nullish(),
    contains: z.string().nullish(),
    eq: z.string().nullish(),
    ge: z.string().nullish(),
    gt: z.string().nullish(),
    le: z.string().nullish(),
    lt: z.string().nullish(),
    ne: z.string().nullish(),
    notContains: z.string().nullish(),
  });
}

export function ModelIDKeyConditionInputSchema(): z.ZodObject<Properties<Types.ModelIDKeyConditionInput>> {
  return z.object({
    beginsWith: z.string().nullish(),
    between: z.array(z.string().nullable()).nullish(),
    eq: z.string().nullish(),
    ge: z.string().nullish(),
    gt: z.string().nullish(),
    le: z.string().nullish(),
    lt: z.string().nullish(),
  });
}

export function ModelIntFilterInputSchema(): z.ZodObject<Properties<Types.ModelIntFilterInput>> {
  return z.object({
    between: z.array(z.number().nullable()).nullish(),
    eq: z.number().nullish(),
    ge: z.number().nullish(),
    gt: z.number().nullish(),
    le: z.number().nullish(),
    lt: z.number().nullish(),
    ne: z.number().nullish(),
  });
}

export function ModelLgpHeroStickyWidgetFilterInputSchema(): z.ZodObject<
  Properties<Types.ModelLgpHeroStickyWidgetFilterInput>
> {
  return z.object({
    and: z.array(z.lazy(() => ModelLgpHeroStickyWidgetFilterInputSchema().nullable())).nullish(),
    competitionId: z.lazy(() => ModelIDFilterInputSchema().nullish()),
    gpId: z.lazy(() => ModelIDFilterInputSchema().nullish()),
    not: z.lazy(() => ModelLgpHeroStickyWidgetFilterInputSchema().nullish()),
    or: z.array(z.lazy(() => ModelLgpHeroStickyWidgetFilterInputSchema().nullable())).nullish(),
    seasonAndCompId: z.lazy(() => ModelIDFilterInputSchema().nullish()),
    seasonId: z.lazy(() => ModelIDFilterInputSchema().nullish()),
  });
}

export function ModelLgpHeroWidgetFilterInputSchema(): z.ZodObject<Properties<Types.ModelLgpHeroWidgetFilterInput>> {
  return z.object({
    and: z.array(z.lazy(() => ModelLgpHeroWidgetFilterInputSchema().nullable())).nullish(),
    competitionId: z.lazy(() => ModelIDFilterInputSchema().nullish()),
    gpId: z.lazy(() => ModelIDFilterInputSchema().nullish()),
    not: z.lazy(() => ModelLgpHeroWidgetFilterInputSchema().nullish()),
    or: z.array(z.lazy(() => ModelLgpHeroWidgetFilterInputSchema().nullable())).nullish(),
    seasonAndCompId: z.lazy(() => ModelIDFilterInputSchema().nullish()),
    seasonId: z.lazy(() => ModelIDFilterInputSchema().nullish()),
  });
}

export function ModelLgpPreviewWidgetFilterInputSchema(): z.ZodObject<
  Properties<Types.ModelLgpPreviewWidgetFilterInput>
> {
  return z.object({
    and: z.array(z.lazy(() => ModelLgpPreviewWidgetFilterInputSchema().nullable())).nullish(),
    competitionId: z.lazy(() => ModelIDFilterInputSchema().nullish()),
    gpId: z.lazy(() => ModelIDFilterInputSchema().nullish()),
    gpName: z.lazy(() => ModelStringFilterInputSchema().nullish()),
    gpStartDateTime: z.lazy(() => ModelStringFilterInputSchema().nullish()),
    laps: z.lazy(() => ModelStringFilterInputSchema().nullish()),
    lengthKm: z.lazy(() => ModelStringFilterInputSchema().nullish()),
    not: z.lazy(() => ModelLgpPreviewWidgetFilterInputSchema().nullish()),
    or: z.array(z.lazy(() => ModelLgpPreviewWidgetFilterInputSchema().nullable())).nullish(),
    seasonAndCompId: z.lazy(() => ModelIDFilterInputSchema().nullish()),
    seasonId: z.lazy(() => ModelIDFilterInputSchema().nullish()),
    sportLabel: z.lazy(() => ModelStringFilterInputSchema().nullish()),
  });
}

export function ModelLgpResultsContainerWidgetFilterInputSchema(): z.ZodObject<
  Properties<Types.ModelLgpResultsContainerWidgetFilterInput>
> {
  return z.object({
    and: z.array(z.lazy(() => ModelLgpResultsContainerWidgetFilterInputSchema().nullable())).nullish(),
    competitionId: z.lazy(() => ModelIDFilterInputSchema().nullish()),
    gpId: z.lazy(() => ModelIDFilterInputSchema().nullish()),
    not: z.lazy(() => ModelLgpResultsContainerWidgetFilterInputSchema().nullish()),
    or: z.array(z.lazy(() => ModelLgpResultsContainerWidgetFilterInputSchema().nullable())).nullish(),
    seasonAndCompId: z.lazy(() => ModelIDFilterInputSchema().nullish()),
    seasonId: z.lazy(() => ModelIDFilterInputSchema().nullish()),
  });
}

export function ModelLgpResultsWidgetFilterInputSchema(): z.ZodObject<
  Properties<Types.ModelLgpResultsWidgetFilterInput>
> {
  return z.object({
    and: z.array(z.lazy(() => ModelLgpResultsWidgetFilterInputSchema().nullable())).nullish(),
    competitionId: z.lazy(() => ModelIDFilterInputSchema().nullish()),
    gpId: z.lazy(() => ModelIDFilterInputSchema().nullish()),
    gpStatus: z.lazy(() => ModelStringFilterInputSchema().nullish()),
    isCurrent: z.lazy(() => ModelBooleanFilterInputSchema().nullish()),
    not: z.lazy(() => ModelLgpResultsWidgetFilterInputSchema().nullish()),
    or: z.array(z.lazy(() => ModelLgpResultsWidgetFilterInputSchema().nullable())).nullish(),
    seasonAndCompId: z.lazy(() => ModelIDFilterInputSchema().nullish()),
    seasonId: z.lazy(() => ModelIDFilterInputSchema().nullish()),
    sessionName: z.lazy(() => ModelStringFilterInputSchema().nullish()),
  });
}

export function ModelLiveScoreWidgetFilterInputSchema(): z.ZodObject<
  Properties<Types.ModelLiveScoreWidgetFilterInput>
> {
  return z.object({
    and: z.array(z.lazy(() => ModelLiveScoreWidgetFilterInputSchema().nullable())).nullish(),
    id: z.lazy(() => ModelIDFilterInputSchema().nullish()),
    not: z.lazy(() => ModelLiveScoreWidgetFilterInputSchema().nullish()),
    or: z.array(z.lazy(() => ModelLiveScoreWidgetFilterInputSchema().nullable())).nullish(),
  });
}

export function ModelSeasonFilterInputSchema(): z.ZodObject<Properties<Types.ModelSeasonFilterInput>> {
  return z.object({
    and: z.array(z.lazy(() => ModelSeasonFilterInputSchema().nullable())).nullish(),
    not: z.lazy(() => ModelSeasonFilterInputSchema().nullish()),
    or: z.array(z.lazy(() => ModelSeasonFilterInputSchema().nullable())).nullish(),
    seasonId: z.lazy(() => ModelIDFilterInputSchema().nullish()),
    seasonJson: z.lazy(() => ModelStringFilterInputSchema().nullish()),
  });
}

export function ModelSeasonRankingGalleryWidgetFilterInputSchema(): z.ZodObject<
  Properties<Types.ModelSeasonRankingGalleryWidgetFilterInput>
> {
  return z.object({
    and: z.array(z.lazy(() => ModelSeasonRankingGalleryWidgetFilterInputSchema().nullable())).nullish(),
    competitionId: z.lazy(() => ModelIDFilterInputSchema().nullish()),
    competitionName: z.lazy(() => ModelStringFilterInputSchema().nullish()),
    competitionsSlugName: z.lazy(() => ModelStringFilterInputSchema().nullish()),
    not: z.lazy(() => ModelSeasonRankingGalleryWidgetFilterInputSchema().nullish()),
    or: z.array(z.lazy(() => ModelSeasonRankingGalleryWidgetFilterInputSchema().nullable())).nullish(),
    seasonAndCompId: z.lazy(() => ModelIDFilterInputSchema().nullish()),
    seasonId: z.lazy(() => ModelIDFilterInputSchema().nullish()),
  });
}

export function ModelSessionFilterInputSchema(): z.ZodObject<Properties<Types.ModelSessionFilterInput>> {
  return z.object({
    and: z.array(z.lazy(() => ModelSessionFilterInputSchema().nullable())).nullish(),
    date: z.lazy(() => ModelStringFilterInputSchema().nullish()),
    gpId: z.lazy(() => ModelIDFilterInputSchema().nullish()),
    isCurrent: z.lazy(() => ModelBooleanFilterInputSchema().nullish()),
    not: z.lazy(() => ModelSessionFilterInputSchema().nullish()),
    or: z.array(z.lazy(() => ModelSessionFilterInputSchema().nullable())).nullish(),
    seasonAndCompId: z.lazy(() => ModelIDFilterInputSchema().nullish()),
    sessionId: z.lazy(() => ModelIDFilterInputSchema().nullish()),
    sessionJson: z.lazy(() => ModelStringFilterInputSchema().nullish()),
    status: z.lazy(() => ModelStringFilterInputSchema().nullish()),
  });
}

export function ModelStringFilterInputSchema(): z.ZodObject<Properties<Types.ModelStringFilterInput>> {
  return z.object({
    beginsWith: z.string().nullish(),
    between: z.array(z.string().nullable()).nullish(),
    contains: z.string().nullish(),
    eq: z.string().nullish(),
    ge: z.string().nullish(),
    gt: z.string().nullish(),
    le: z.string().nullish(),
    lt: z.string().nullish(),
    ne: z.string().nullish(),
    notContains: z.string().nullish(),
  });
}

export function ModelStringKeyConditionInputSchema(): z.ZodObject<Properties<Types.ModelStringKeyConditionInput>> {
  return z.object({
    beginsWith: z.string().nullish(),
    between: z.array(z.string().nullable()).nullish(),
    eq: z.string().nullish(),
    ge: z.string().nullish(),
    gt: z.string().nullish(),
    le: z.string().nullish(),
    lt: z.string().nullish(),
  });
}

export function ModelTeamDriverPageWidgetFilterInputSchema(): z.ZodObject<
  Properties<Types.ModelTeamDriverPageWidgetFilterInput>
> {
  return z.object({
    and: z.array(z.lazy(() => ModelTeamDriverPageWidgetFilterInputSchema().nullable())).nullish(),
    competitionId: z.lazy(() => ModelIDFilterInputSchema().nullish()),
    not: z.lazy(() => ModelTeamDriverPageWidgetFilterInputSchema().nullish()),
    or: z.array(z.lazy(() => ModelTeamDriverPageWidgetFilterInputSchema().nullable())).nullish(),
    seasonAndCompId: z.lazy(() => ModelIDFilterInputSchema().nullish()),
    seasonId: z.lazy(() => ModelIDFilterInputSchema().nullish()),
  });
}

export function ModelTeamFilterInputSchema(): z.ZodObject<Properties<Types.ModelTeamFilterInput>> {
  return z.object({
    and: z.array(z.lazy(() => ModelTeamFilterInputSchema().nullable())).nullish(),
    editorialDataId: z.lazy(() => ModelIDFilterInputSchema().nullish()),
    not: z.lazy(() => ModelTeamFilterInputSchema().nullish()),
    or: z.array(z.lazy(() => ModelTeamFilterInputSchema().nullable())).nullish(),
    seasonAndCompId: z.lazy(() => ModelIDFilterInputSchema().nullish()),
    teamId: z.lazy(() => ModelIDFilterInputSchema().nullish()),
    teamJson: z.lazy(() => ModelStringFilterInputSchema().nullish()),
  });
}

export function ModelTeamRankingWidgetFilterInputSchema(): z.ZodObject<
  Properties<Types.ModelTeamRankingWidgetFilterInput>
> {
  return z.object({
    and: z.array(z.lazy(() => ModelTeamRankingWidgetFilterInputSchema().nullable())).nullish(),
    competitionId: z.lazy(() => ModelIDFilterInputSchema().nullish()),
    competitionLabel: z.lazy(() => ModelStringFilterInputSchema().nullish()),
    not: z.lazy(() => ModelTeamRankingWidgetFilterInputSchema().nullish()),
    or: z.array(z.lazy(() => ModelTeamRankingWidgetFilterInputSchema().nullable())).nullish(),
    seasonAndCompId: z.lazy(() => ModelIDFilterInputSchema().nullish()),
    seasonId: z.lazy(() => ModelIDFilterInputSchema().nullish()),
  });
}

export function S3ObjectSchema(): z.ZodObject<Properties<Types.S3Object>> {
  return z.object({
    content: z.string().nullish(),
    key: z.string().nullish(),
  });
}

export function SeasonDriverRankingInputSchema(): z.ZodObject<Properties<Types.SeasonDriverRankingInput>> {
  return z.object({
    driverFirstname: z.string().nullish(),
    driverId: z.string(),
    driverImageUrl: z.string().nullish(),
    driverPageLink: z.string().nullish(),
    driverSurname: z.string().nullish(),
    logoUrl: z.string().nullish(),
    points: z.string().nullish(),
    position: z.string().nullish(),
    teamId: z.string(),
    teamName: z.string().nullish(),
    teamUrl: z.string().nullish(),
  });
}

export function SeasonTeamRankingInputSchema(): z.ZodObject<Properties<Types.SeasonTeamRankingInput>> {
  return z.object({
    logoUrl: z.string().nullish(),
    points: z.string().nullish(),
    position: z.string().nullish(),
    teamId: z.string(),
    teamImageUrl: z.string().nullish(),
    teamName: z.string().nullish(),
    teamUrl: z.string().nullish(),
  });
}

export function TeamDriverDriverInputSchema(): z.ZodObject<Properties<Types.TeamDriverDriverInput>> {
  return z.object({
    imageUrl: z.string().nullish(),
    name: z.string().nullish(),
    position: z.number().nullish(),
    url: z.string().nullish(),
  });
}

export function TeamDriverTeamInputSchema(): z.ZodObject<Properties<Types.TeamDriverTeamInput>> {
  return z.object({
    drivers: z.array(z.lazy(() => TeamDriverDriverInputSchema().nullable())).nullish(),
    position: z.number().nullish(),
    teamLogoUrl: z.string().nullish(),
    teamName: z.string().nullish(),
    teamUrl: z.string().nullish(),
  });
}

export function TeamRankingTeamInputSchema(): z.ZodObject<Properties<Types.TeamRankingTeamInput>> {
  return z.object({
    customTeamPageUrl: z.string().nullish(),
    pos: z.string().nullish(),
    pts: z.string().nullish(),
    teamName: z.string().nullish(),
  });
}

export function UpdateCardDriverRankingWidgetInputSchema(): z.ZodObject<
  Properties<Types.UpdateCardDriverRankingWidgetInput>
> {
  return z.object({
    competitionId: z.string(),
    customCompetitionName: z.string().nullish(),
    drivers: z.array(z.lazy(() => CardDriverRankingDriverInfoInputSchema().nullable())).nullish(),
    rankingLink: z.string().nullish(),
  });
}

export function UpdateCardGpCalendarWidgetInputSchema(): z.ZodObject<
  Properties<Types.UpdateCardGpCalendarWidgetInput>
> {
  return z.object({
    calendarLink: z.string().nullish(),
    competitionId: z.string(),
    customCompetitionName: z.string().nullish(),
    gpList: z.array(z.lazy(() => CardGpCalendarGpInfoInputSchema().nullable())).nullish(),
  });
}

export function UpdateCardGpResultsWidgetInputSchema(): z.ZodObject<Properties<Types.UpdateCardGpResultsWidgetInput>> {
  return z.object({
    competitionId: z.string(),
    customCompetitionName: z.string().nullish(),
    customGpName: z.string().nullish(),
    isSprint: z.boolean().nullish(),
    results: z.array(z.lazy(() => CardGpResultsResultInfoInputSchema().nullable())).nullish(),
    resultsLink: z.string().nullish(),
  });
}

export function UpdateCardTeamRankingWidgetInputSchema(): z.ZodObject<
  Properties<Types.UpdateCardTeamRankingWidgetInput>
> {
  return z.object({
    competitionId: z.string(),
    customCompetitionName: z.string().nullish(),
    rankingLink: z.string().nullish(),
    teams: z.array(z.lazy(() => CardTeamRankingTeamInfoInputSchema().nullable())).nullish(),
  });
}

export function UpdateCompetitionInputSchema(): z.ZodObject<Properties<Types.UpdateCompetitionInput>> {
  return z.object({
    competitionId: z.string().nullish(),
    competitionJson: z.any().nullish(),
    editorialDataId: z.string().nullish(),
    previousSeasonAndCompId: z.string().nullish(),
    seasonAndCompId: z.string(),
    seasonId: z.string().nullish(),
  });
}

export function UpdateCrossLinkInputSchema(): z.ZodObject<Properties<Types.UpdateCrossLinkInput>> {
  return z.object({
    crossLinkJson: z.any().nullish(),
    gpId: z.string(),
    seasonAndCompId: z.string(),
  });
}

export function UpdateDriverInputSchema(): z.ZodObject<Properties<Types.UpdateDriverInput>> {
  return z.object({
    driverId: z.string(),
    driverJson: z.any().nullish(),
    editorialDataId: z.string().nullish(),
    seasonAndCompId: z.string(),
    teamId: z.string().nullish(),
  });
}

export function UpdateDriverRankingWidgetInputSchema(): z.ZodObject<Properties<Types.UpdateDriverRankingWidgetInput>> {
  return z.object({
    competitionId: z.string().nullish(),
    competitionLabel: z.string().nullish(),
    items: z.array(z.lazy(() => DriverRankingDriverInputSchema().nullable())).nullish(),
    seasonAndCompId: z.string(),
    seasonId: z.string().nullish(),
  });
}

export function UpdateDriverStatsWidgetInputSchema(): z.ZodObject<Properties<Types.UpdateDriverStatsWidgetInput>> {
  return z.object({
    customCompetitionName: z.string().nullish(),
    customSeasonName: z.string().nullish(),
    driverId: z.string(),
    seasonAndCompId: z.string().nullish(),
    statistics: z.array(z.lazy(() => DriverStatInputSchema().nullable())).nullish(),
  });
}

export function UpdateEditorialDataInputSchema(): z.ZodObject<Properties<Types.UpdateEditorialDataInput>> {
  return z.object({
    editorialDataId: z.string(),
    editorialJson: z.any().nullish(),
    isCurrent: z.boolean().nullish(),
    seasonAndCompId: z.string(),
  });
}

export function UpdateGpCalendarWidgetInputSchema(): z.ZodObject<Properties<Types.UpdateGpCalendarWidgetInput>> {
  return z.object({
    competitionId: z.string().nullish(),
    items: z.array(z.lazy(() => GpCalendarItemInputSchema().nullable())).nullish(),
    seasonAndCompId: z.string(),
    seasonId: z.string().nullish(),
  });
}

export function UpdateGpCurrentCalendarWidgetInputSchema(): z.ZodObject<
  Properties<Types.UpdateGpCurrentCalendarWidgetInput>
> {
  return z.object({
    competitionId: z.string().nullish(),
    items: z.array(z.lazy(() => GpCalendarItemInputSchema().nullable())).nullish(),
    seasonAndCompId: z.string(),
    seasonId: z.string().nullish(),
  });
}

export function UpdateGpDataInputSchema(): z.ZodObject<Properties<Types.UpdateGpDataInput>> {
  return z.object({
    competitionId: z.string().nullish(),
    data: z.any().nullish(),
    gpId: z.string(),
    messageType: z.string().nullish(),
    seasonAndCompId: z.string(),
    seasonId: z.string().nullish(),
  });
}

export function UpdateGpLiveContainerWidgetInputSchema(): z.ZodObject<
  Properties<Types.UpdateGpLiveContainerWidgetInput>
> {
  return z.object({
    DS: z.lazy(() => GpLiveContainerWrapperInputSchema().nullish()),
    G: z.lazy(() => GpLiveContainerWrapperInputSchema().nullish()),
    R: z.lazy(() => GpLiveContainerWrapperInputSchema().nullish()),
    TS: z.lazy(() => GpLiveContainerWrapperInputSchema().nullish()),
    competitionId: z.string().nullish(),
    seasonAndCompId: z.string(),
    seasonId: z.string().nullish(),
  });
}

export function UpdateGpLiveHeroWidgetInputSchema(): z.ZodObject<Properties<Types.UpdateGpLiveHeroWidgetInput>> {
  return z.object({
    competitionId: z.string().nullish(),
    gpInfo: z.lazy(() => GpLiveHeroGpInfoInputSchema().nullish()),
    isCurrent: z.boolean().nullish(),
    seasonAndCompId: z.string(),
    seasonId: z.string().nullish(),
  });
}

export function UpdateGpPodiumListWidgetInputSchema(): z.ZodObject<Properties<Types.UpdateGpPodiumListWidgetInput>> {
  return z.object({
    competitionId: z.string().nullish(),
    grandPrixList: z.array(z.lazy(() => GpPodiumListGrandPrixInputSchema().nullable())).nullish(),
    seasonAndCompId: z.string(),
    seasonId: z.string().nullish(),
  });
}

export function UpdateGpResultsContainerWidgetInputSchema(): z.ZodObject<
  Properties<Types.UpdateGpResultsContainerWidgetInput>
> {
  return z.object({
    FP1: z.lazy(() => GpResultsContainerWrapperInputSchema().nullish()),
    FP2: z.lazy(() => GpResultsContainerWrapperInputSchema().nullish()),
    FP3: z.lazy(() => GpResultsContainerWrapperInputSchema().nullish()),
    FP4: z.lazy(() => GpResultsContainerWrapperInputSchema().nullish()),
    G: z.lazy(() => GpResultsContainerWrapperInputSchema().nullish()),
    GS: z.lazy(() => GpResultsContainerWrapperInputSchema().nullish()),
    Q: z.lazy(() => GpResultsContainerWrapperInputSchema().nullish()),
    QS: z.lazy(() => GpResultsContainerWrapperInputSchema().nullish()),
    R: z.lazy(() => GpResultsContainerWrapperInputSchema().nullish()),
    SPRINT: z.lazy(() => GpResultsContainerWrapperInputSchema().nullish()),
    SS: z.lazy(() => GpResultsContainerWrapperInputSchema().nullish()),
    competitionId: z.string().nullish(),
    gpId: z.string(),
    seasonAndCompId: z.string(),
    seasonId: z.string().nullish(),
  });
}

export function UpdateGpResultsWidgetInputSchema(): z.ZodObject<Properties<Types.UpdateGpResultsWidgetInput>> {
  return z.object({
    competitionId: z.string().nullish(),
    gpId: z.string(),
    gpInfo: z.lazy(() => GpResultsGpInfoInputSchema().nullish()),
    gpStatus: z.string().nullish(),
    isCurrent: z.boolean().nullish(),
    seasonAndCompId: z.string().nullish(),
    seasonId: z.string().nullish(),
    sessionName: z.string(),
    sessions: z.array(z.lazy(() => GpResultsSessionInputSchema().nullable())).nullish(),
  });
}

export function UpdateGrandPrixInputSchema(): z.ZodObject<Properties<Types.UpdateGrandPrixInput>> {
  return z.object({
    date: z.string().datetime().nullish(),
    editorialDataId: z.string().nullish(),
    gpId: z.string(),
    gpJson: z.any().nullish(),
    isCurrent: z.boolean().nullish(),
    seasonAndCompId: z.string(),
    status: z.string().nullish(),
  });
}

export function UpdateLgpHeroStickyWidgetInputSchema(): z.ZodObject<Properties<Types.UpdateLgpHeroStickyWidgetInput>> {
  return z.object({
    competitionId: z.string().nullish(),
    gpId: z.string(),
    info: z.lazy(() => LgpHeroStickyInfoInputSchema().nullish()),
    seasonAndCompId: z.string(),
    seasonId: z.string().nullish(),
    session: z.lazy(() => LgpHeroStickySessionInputSchema().nullish()),
  });
}

export function UpdateLgpHeroWidgetInputSchema(): z.ZodObject<Properties<Types.UpdateLgpHeroWidgetInput>> {
  return z.object({
    competitionId: z.string().nullish(),
    gpId: z.string(),
    info: z.lazy(() => LgpHeroInfoInputSchema().nullish()),
    seasonAndCompId: z.string(),
    seasonId: z.string().nullish(),
    session: z.lazy(() => LgpHeroSessionInputSchema().nullish()),
  });
}

export function UpdateLgpPreviewWidgetInputSchema(): z.ZodObject<Properties<Types.UpdateLgpPreviewWidgetInput>> {
  return z.object({
    calendar: z.lazy(() => LgpInfoLinkInputSchema().nullish()),
    competitionId: z.string().nullish(),
    gpId: z.string(),
    gpName: z.string().nullish(),
    gpStartDateTime: z.string().nullish(),
    laps: z.string().nullish(),
    lengthKm: z.string().nullish(),
    news: z.lazy(() => LgpInfoLinkInputSchema().nullish()),
    ranking: z.lazy(() => LgpInfoLinkInputSchema().nullish()),
    results: z.lazy(() => LgpInfoLinkInputSchema().nullish()),
    seasonAndCompId: z.string(),
    seasonId: z.string().nullish(),
    sessions: z.array(z.lazy(() => LgpPreviewSessionInputSchema().nullable())).nullish(),
    sportLabel: z.string().nullish(),
  });
}

export function UpdateLgpResultsContainerWidgetInputSchema(): z.ZodObject<
  Properties<Types.UpdateLgpResultsContainerWidgetInput>
> {
  return z.object({
    FP1: z.lazy(() => LgpResultsContainerWrapperInputSchema().nullish()),
    FP2: z.lazy(() => LgpResultsContainerWrapperInputSchema().nullish()),
    FP3: z.lazy(() => LgpResultsContainerWrapperInputSchema().nullish()),
    FP4: z.lazy(() => LgpResultsContainerWrapperInputSchema().nullish()),
    G: z.lazy(() => LgpResultsContainerWrapperInputSchema().nullish()),
    GS: z.lazy(() => LgpResultsContainerWrapperInputSchema().nullish()),
    Q: z.lazy(() => LgpResultsContainerWrapperInputSchema().nullish()),
    QS: z.lazy(() => LgpResultsContainerWrapperInputSchema().nullish()),
    R: z.lazy(() => LgpResultsContainerWrapperInputSchema().nullish()),
    SPRINT: z.lazy(() => LgpResultsContainerWrapperInputSchema().nullish()),
    SS: z.lazy(() => LgpResultsContainerWrapperInputSchema().nullish()),
    competitionId: z.string().nullish(),
    gpId: z.string(),
    seasonAndCompId: z.string(),
    seasonId: z.string().nullish(),
  });
}

export function UpdateLgpResultsWidgetInputSchema(): z.ZodObject<Properties<Types.UpdateLgpResultsWidgetInput>> {
  return z.object({
    calendar: z.lazy(() => LgpInfoLinkInputSchema().nullish()),
    competitionId: z.string().nullish(),
    gpId: z.string(),
    gpInfo: z.lazy(() => LgpResultsGpInfoInputSchema().nullish()),
    gpStatus: z.string().nullish(),
    isCurrent: z.boolean().nullish(),
    ranking: z.lazy(() => LgpInfoLinkInputSchema().nullish()),
    results: z.lazy(() => LgpInfoLinkInputSchema().nullish()),
    seasonAndCompId: z.string().nullish(),
    seasonId: z.string().nullish(),
    sessionName: z.string(),
    sessions: z.array(z.lazy(() => LgpResultsSessionInputSchema().nullable())).nullish(),
  });
}

export function UpdateLiveScoreWidgetInputSchema(): z.ZodObject<Properties<Types.UpdateLiveScoreWidgetInput>> {
  return z.object({
    currentDay: z.array(z.lazy(() => LiveScoreWidgetDayInputSchema().nullable())).nullish(),
    id: z.string(),
    nextDay: z.array(z.lazy(() => LiveScoreWidgetDayInputSchema().nullable())).nullish(),
    previousDay: z.array(z.lazy(() => LiveScoreWidgetDayInputSchema().nullable())).nullish(),
  });
}

export function UpdateSeasonInputSchema(): z.ZodObject<Properties<Types.UpdateSeasonInput>> {
  return z.object({
    seasonId: z.string(),
    seasonJson: z.any().nullish(),
  });
}

export function UpdateSeasonRankingGalleryWidgetInputSchema(): z.ZodObject<
  Properties<Types.UpdateSeasonRankingGalleryWidgetInput>
> {
  return z.object({
    competitionId: z.string().nullish(),
    competitionName: z.string().nullish(),
    competitionsSlugName: z.string().nullish(),
    drivers: z.array(z.lazy(() => SeasonDriverRankingInputSchema().nullable())).nullish(),
    seasonAndCompId: z.string(),
    seasonId: z.string().nullish(),
    teams: z.array(z.lazy(() => SeasonTeamRankingInputSchema().nullable())).nullish(),
  });
}

export function UpdateSessionInputSchema(): z.ZodObject<Properties<Types.UpdateSessionInput>> {
  return z.object({
    date: z.string().datetime().nullish(),
    gpId: z.string(),
    isCurrent: z.boolean().nullish(),
    seasonAndCompId: z.string().nullish(),
    sessionId: z.string(),
    sessionJson: z.any().nullish(),
    status: z.string().nullish(),
  });
}

export function UpdateTeamDriverPageWidgetInputSchema(): z.ZodObject<
  Properties<Types.UpdateTeamDriverPageWidgetInput>
> {
  return z.object({
    competitionId: z.string().nullish(),
    seasonAndCompId: z.string(),
    seasonId: z.string().nullish(),
    teamList: z.array(z.lazy(() => TeamDriverTeamInputSchema().nullable())).nullish(),
  });
}

export function UpdateTeamInputSchema(): z.ZodObject<Properties<Types.UpdateTeamInput>> {
  return z.object({
    editorialDataId: z.string().nullish(),
    seasonAndCompId: z.string(),
    teamId: z.string(),
    teamJson: z.any().nullish(),
  });
}

export function UpdateTeamRankingWidgetInputSchema(): z.ZodObject<Properties<Types.UpdateTeamRankingWidgetInput>> {
  return z.object({
    competitionId: z.string().nullish(),
    competitionLabel: z.string().nullish(),
    items: z.array(z.lazy(() => TeamRankingTeamInputSchema().nullable())).nullish(),
    seasonAndCompId: z.string(),
    seasonId: z.string().nullish(),
  });
}
